import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SEOHead from '@/components/seo/SEOHead';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Search, Home, Printer, FileText, HelpCircle, ArrowRight } from 'lucide-react';

export default function NotFoundPage() {
  const [query, setQuery] = useState('');
  const navigate = useNavigate();

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/faq?q=${encodeURIComponent(query)}`);
    }
  };

  const popularLinks = [
    { name: 'Print PDF Online', url: '/print-pdf-online' },
    { name: 'Print Documents Online', url: '/print-documents-online' },
    { name: 'Online Xerox', url: '/online-xerox' },
    { name: 'Black & White Printing', url: '/black-and-white-printing' },
    { name: 'Color Printing', url: '/color-printing' },
    { name: 'Spiral Binding', url: '/spiral-binding' },
    { name: 'Help & FAQs', url: '/faq' },
    { name: 'SEO Blog', url: '/blog' }
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col justify-between antialiased">
      <SEOHead
        title="404 Page Not Found | Inchpaper Print"
        description="The requested document page could not be found. Search our print services or start printing online now."
        robots="noindex, follow"
      />

      <Navbar />

      <main className="grow max-w-4xl mx-auto px-4 py-16 text-center space-y-8">
        <div className="space-y-3">
          <div className="text-6xl sm:text-7xl font-extrabold text-[#7d0909]">404</div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
            Page Not Found
          </h1>
          <p className="text-slate-600 text-sm sm:text-base max-w-md mx-auto">
            The page or service link you requested may have moved or been renamed. Try searching below or explore popular services.
          </p>
        </div>

        {/* Search Bar */}
        <form onSubmit={handleSearch} className="max-w-md mx-auto flex items-center gap-2">
          <div className="relative grow">
            <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search print services or FAQs..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full bg-white border border-slate-300 rounded-xl pl-10 pr-4 py-2.5 text-sm focus:ring-2 focus:ring-red-700 focus:outline-hidden"
            />
          </div>
          <Button type="submit" className="bg-[#7d0909] hover:bg-[#5e0707] text-white rounded-xl text-xs font-bold px-4 py-2.5">
            Search
          </Button>
        </form>

        {/* Popular Links */}
        <div className="bg-white p-6 rounded-2xl border border-slate-200/80 shadow-xs max-w-2xl mx-auto space-y-4">
          <h2 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Popular Service Destinations</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs font-medium">
            {popularLinks.map((link) => (
              <Link
                key={link.url}
                to={link.url}
                className="p-2 bg-slate-50 hover:bg-red-50 hover:text-red-800 rounded-lg text-slate-700 transition-colors truncate border border-slate-100"
              >
                {link.name}
              </Link>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap items-center justify-center gap-4">
          <Link to="/">
            <Button className="bg-[#7d0909] hover:bg-[#5e0707] text-white rounded-full px-6 py-3 font-semibold text-sm flex items-center gap-2">
              <Home className="w-4 h-4" />
              <span>Return Home</span>
            </Button>
          </Link>

          <Link to="/#upload">
            <Button variant="outline" className="rounded-full px-6 py-3 font-semibold text-sm flex items-center gap-2">
              <Printer className="w-4 h-4" />
              <span>Upload Document</span>
            </Button>
          </Link>
        </div>
      </main>

      <Footer />
    </div>
  );
}
